import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './Projects.css';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [expandedArchitecture, setExpandedArchitecture] = useState({});
  const [baseHeight, setBaseHeight] = useState(null);
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const contentRef = useRef([]);

  const toggleArchitecture = (index) => {
    setExpandedArchitecture(prev => {
      if (prev[index]) {
        return {};
      } else {
        return { [index]: true };
      }
    });
  };

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: isMobile ? '0px' : '-150px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Bloquer le défilement quand le modal d'image est ouvert
    if (expandedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [expandedImage]);

  useEffect(() => {
    // Calculer la hauteur maximale du contenu (image + titre + description)
    const calculateHeight = () => {
      if (contentRef.current.length > 0) {
        // Petit délai pour s'assurer que le DOM est complètement rendu
        setTimeout(() => {
          const heights = contentRef.current.map(el => el?.offsetHeight || 0);
          const maxHeight = Math.max(...heights);
          if (maxHeight > 0) {
            setBaseHeight(maxHeight);
          }
        }, 100);
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [isVisible]);

  const projects = [
    {
      name: 'Assistant visite 3D',
      image: '/photo/projet/Visite_3D_Chatbot.png',
      description: [
        "Compréhension spatiale de l'appartement",
        "Enrichie par les datas du client",
        "Mode vocal disponible",
        "Génération de contenus synthétiques (comparatifs, points forts/faibles)",
        "Soumission de photo possible"
      ],
      architecture: [
        "Pipeline de transcription/metadata depuis la visite 3D",
        "Structuration des données métier dans un graphe de connaissances (Neo4j)",
        "RAG hybride : vector DB (FAISS) + graphe",
        "Queue pour la conversation orales -> arrêter l'oral quand on veut"
      ]
    },
    {
      name: 'Logiciel de scan de bulletin de paie',
      image: '/photo/projet/bulletin_de_paye_photo.png',
      description: [
        "Système d'audit automatique des bulletins de paie",
        "Extraction OCR des données",
        "Vérification des calculs",
        "Vérication par rapport aux conventions collectives"
      ],
      architecture: [
        "OCR + parsing structuré (PDF → JSON normalisé)",
        "Système multi-agent avec un agent manager",
        "Agent déterministe : vérification des calculs",
        "Agent variable : contrôle via RAG sur conventions collectives",
        "Génération d'un rapport explicatif"
      ]
    },
    {
      name: 'Scrapping annonce avec IA',
      image: '/photo/projet/scrap_web_image.png',
      description: [
        "Collecte automatique de toutes les annonces immobilières de la Guadeloupe",
        "Mise a jour des annonces du site web",
        "Structuration des données"
      ],
      architecture: [
        "Contournement des captcha google",
        "Filtre IA pour enlever des annonces en fonctions de certain critère",
        "Déclenchement automatique du programme héberger sur serveur",
      ]
    },
    {
      name: 'Assistant IA cours en ligne',
      image: null,
      description: [
        "Ecoute en temps reel",
        "Evaluation du niveau de l'élève",
        "Répond aux questions de l'élève",
        "S'appuie sur le cours du professeur pour répondre"
      ],
      architecture: [
        "Stream audio → transcription temps réel (speech to text)",
        "Segmentation + extraction des topics (embeddings)",
        "Création d'une application zoom",
      ]
    }
  ];

  return (
    <>
    <section id="projects" className={`projects ${isVisible ? 'visible' : ''}`} ref={sectionRef}>
      <div className="projects-container">
        <h2 className={`section-title fade-in-up ${isVisible ? 'visible' : ''}`}><span className="highlight-exemples">Exemples</span> de solutions IA déployées</h2>
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div 
              key={index} 
              className={`project-card fade-in-up delay-${(index % 2 + 1) * 100} ${isVisible ? 'visible' : ''}`}
              ref={el => cardsRef.current[index] = el}
            >
              <div 
                className="project-content-wrapper"
                ref={el => contentRef.current[index] = el}
                style={{ height: baseHeight ? `${baseHeight}px` : 'auto' }}
              >
                {project.image ? (
                  <div className="project-image-container">
                    <img src={project.image} alt={project.name} className="project-image" />
                    <button 
                      className="expand-button" 
                      onClick={() => setExpandedImage(project.image)}
                      aria-label="Agrandir l'image"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="project-placeholder">En développement</div>
                )}
                <h3 className="project-name">{project.name}</h3>
                <div className="project-section description-section">
                  <h4 className="project-section-title">Description</h4>
                  <ul className="project-list">
                    {project.description.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <button 
                className="toggle-architecture-btn"
                onClick={() => toggleArchitecture(index)}
              >
                {expandedArchitecture[index] ? 'Masquer l’architecture' : 'Voir l’architecture'}
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{ 
                    transform: expandedArchitecture[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {expandedArchitecture[index] && (
                <div className="project-section architecture-section">
                  <h4 className="project-section-title">Architecture</h4>
                  <ul className="project-list">
                    {project.architecture.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
    {expandedImage && ReactDOM.createPortal(
      <div className="image-modal" onClick={() => setExpandedImage(null)}>
        <button className="close-modal" onClick={() => setExpandedImage(null)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
          <img src={expandedImage} alt="Image agrandie" />
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default Projects;
